import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Briefcase, TrendingUp, Award, Wallet, ArrowRight, Target, Link2, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
    className="glass rounded-xl p-5"
    data-testid={testid}
  >
    <div className="flex items-center justify-between">
      <div className="w-10 h-10 rounded-lg bg-[#ff7a00]/10 border border-[#ff7a00]/30 flex items-center justify-center text-[#ff7a00]">
        <Icon size={18} />
      </div>
    </div>
    <div className="mt-4 font-display text-2xl font-bold text-[#f5f5f5]">{value}</div>
    <div className="text-xs text-[#8a8a8a] mt-1 uppercase tracking-wider">{label}</div>
    {sub && <div className="text-xs text-[#ff9a2f] mt-1">{sub}</div>}
  </motion.div>
);

export const OverviewPage = () => {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [certs, setCerts] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [aff, setAff] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/accounts"),
      api.get("/payments/orders"),
      api.get("/certificates"),
      api.get("/payouts"),
      api.get("/affiliate/me"),
    ]).then(([a, o, c, p, af]) => {
      setAccounts(a.data || []);
      setOrders(o.data || []);
      setCerts(c.data || []);
      setPayouts(p.data || []);
      setAff(af.data || null);
    }).catch(() => {});
  }, []);

  const copyRef = () => {
    if (!aff?.referral_link) return;
    navigator.clipboard.writeText(aff.referral_link);
    setCopied(true);
    toast.success("Referral link copied");
    setTimeout(() => setCopied(false), 1500);
  };

  const activeAccount = accounts.find(a => a.status === "Active" || a.status === "Funded") || accounts[0];
  const totalFunded = accounts.filter(a => a.status === "Funded").reduce((s, a) => s + a.account_size, 0);
  const totalPnl = accounts.reduce((s, a) => s + (a.current_pnl || 0), 0);
  const totalPaid = payouts.filter(p => p.status === "paid").reduce((s, p) => s + p.amount, 0);

  return (
    <div className="space-y-8" data-testid="overview-page">
      <div>
        <div className="text-[#8a8a8a] text-xs tracking-[0.25em] uppercase">Welcome back</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">
          {user?.first_name || "Trader"}, <span className="text-[#ff7a00]">make it count.</span>
        </h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Briefcase} label="Active Accounts" value={accounts.length} sub={`${accounts.filter(a => a.status === "Funded").length} funded`} testid="stat-accounts" />
        <StatCard icon={TrendingUp} label="Funded Capital" value={`$${totalFunded.toLocaleString()}`} testid="stat-funded-capital" />
        <StatCard icon={Target} label="Total P&L" value={`${totalPnl >= 0 ? "+" : ""}$${totalPnl.toLocaleString()}`} sub={totalPnl >= 0 ? "in profit" : "in drawdown"} testid="stat-pnl" />
        <StatCard icon={Wallet} label="Total Paid Out" value={`$${totalPaid.toLocaleString()}`} testid="stat-paid-out" />
      </div>

      {/* Affiliate quick-widget */}
      {aff && (
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="glass rounded-xl p-5 sm:p-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-6"
          data-testid="overview-affiliate-widget"
        >
          <div className="w-12 h-12 rounded-lg bg-[#ff7a00]/10 border border-[#ff7a00]/30 flex items-center justify-center text-[#ff7a00] shrink-0">
            <Link2 size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-display text-base sm:text-lg font-semibold">Earn 10% on every referral</div>
              <Badge className="bg-[#ff7a00]/15 text-[#ff9a2f] border-[#ff7a00]/30 text-[10px] uppercase tracking-widest">Live</Badge>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <div className="flex-1 min-w-0 glass rounded-md px-3 py-2 font-mono text-xs sm:text-sm text-[#f5f5f5] truncate" data-testid="overview-referral-link">
                {aff.referral_link}
              </div>
              <Button
                size="sm"
                onClick={copyRef}
                className="bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold h-9 px-3 shrink-0"
                data-testid="overview-copy-referral"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                <span className="ml-1.5 hidden sm:inline">{copied ? "Copied" : "Copy"}</span>
              </Button>
            </div>
            <div className="mt-2 text-[11px] text-[#8a8a8a] flex flex-wrap gap-x-4 gap-y-1">
              <span>Code <span className="font-mono text-[#ff9a2f] font-semibold">{aff.referral_code}</span></span>
              <span>Earned <span className="text-[#f5f5f5] font-semibold">${aff.total_earned.toFixed(2)}</span></span>
              <span>Available <span className="text-[#f5f5f5] font-semibold">${aff.available_balance.toFixed(2)}</span></span>
              <span>Referrals <span className="text-[#f5f5f5] font-semibold">{aff.total_referrals}</span></span>
            </div>
          </div>
          <Link to="/dashboard/affiliate" data-testid="overview-affiliate-link">
            <Button variant="outline" className="border-[#ff7a00]/30 text-[#ff9a2f] hover:bg-[#ff7a00]/10 hover:text-[#ff7a00] h-11">
              Open Dashboard <ArrowRight size={14} className="ml-1.5" />
            </Button>
          </Link>
        </motion.div>
      )}

      {/* Active account spotlight */}
      {activeAccount ? (
        <div className="glass rounded-xl p-6" data-testid="active-account-card">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-[10px] text-[#8a8a8a] uppercase tracking-widest">Active Account</div>
              <div className="font-display text-2xl font-bold mt-1">{activeAccount.platform} · ${activeAccount.account_size.toLocaleString()}</div>
            </div>
            <Badge className={`uppercase text-[10px] tracking-widest ${
              activeAccount.status === "Funded" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" :
              activeAccount.status === "Passed" ? "bg-[#ff7a00]/15 text-[#ff7a00] border-[#ff7a00]/30" :
              activeAccount.status === "Failed" ? "bg-red-500/15 text-red-400 border-red-500/30" :
              "bg-[#ff7a00]/10 text-[#ff9a2f] border-[#ff7a00]/30"
            }`}>{activeAccount.status}</Badge>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            <div>
              <div className="text-xs text-[#8a8a8a] uppercase tracking-wider mb-2">Profit Target</div>
              <div className="text-xl font-display font-semibold text-[#ff9a2f]">{activeAccount.profit_target_pct}%</div>
              <Progress value={Math.min(100, Math.max(0, (activeAccount.current_pnl / (activeAccount.account_size * activeAccount.profit_target_pct / 100)) * 100))} className="mt-2 h-1.5 bg-[#0a0a0a]" />
            </div>
            <div>
              <div className="text-xs text-[#8a8a8a] uppercase tracking-wider mb-2">Daily Drawdown</div>
              <div className="text-xl font-display font-semibold">{activeAccount.daily_drawdown_pct}%</div>
              <div className="text-[10px] text-[#8a8a8a] mt-2">Limit ${(activeAccount.account_size * activeAccount.daily_drawdown_pct / 100).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs text-[#8a8a8a] uppercase tracking-wider mb-2">Max Drawdown</div>
              <div className="text-xl font-display font-semibold">{activeAccount.max_drawdown_pct}%</div>
              <div className="text-[10px] text-[#8a8a8a] mt-2">Limit ${(activeAccount.account_size * activeAccount.max_drawdown_pct / 100).toLocaleString()}</div>
            </div>
          </div>
          <div className="mt-6 pt-5 border-t border-[#ff7a00]/15 flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-[#8a8a8a]">
              Login ID: <span className="text-[#f5f5f5] font-mono">{activeAccount.login_id}</span> · Server: <span className="text-[#f5f5f5] font-mono">{activeAccount.server}</span>
            </div>
            <Link to="/dashboard/accounts" data-testid="overview-view-accounts">
              <Button size="sm" className="bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold">
                View Account <ArrowRight size={16} className="ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
      ) : (
        <div className="glass rounded-xl p-8 text-center" data-testid="no-accounts-state">
          <Briefcase className="mx-auto text-[#ff7a00]" size={36} />
          <div className="font-display text-2xl font-bold mt-4">No trading accounts yet</div>
          <p className="text-[#8a8a8a] text-sm mt-2 max-w-md mx-auto">Purchase a challenge to get started. Once paid, our team assigns your trading credentials within hours.</p>
          <Link to="/challenges" data-testid="overview-buy-challenge">
            <Button className="mt-6 bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold">
              View Challenges <ArrowRight size={16} className="ml-1.5" />
            </Button>
          </Link>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="glass rounded-xl p-6" data-testid="recent-orders-card">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display text-lg font-semibold">Recent Orders</div>
            <Link to="/dashboard/orders" className="text-xs text-[#ff9a2f] hover:text-[#ff7a00]">View all</Link>
          </div>
          {orders.length === 0 && <div className="text-sm text-[#8a8a8a]">No orders yet.</div>}
          <div className="space-y-2">
            {orders.slice(0, 4).map((o) => (
              <div key={o.id} className="flex items-center justify-between text-sm p-2.5 rounded-lg hover:bg-white/5">
                <div>
                  <div className="text-[#f5f5f5]">{o.challenge_name}</div>
                  <div className="text-[10px] text-[#8a8a8a] uppercase tracking-widest">${o.account_size.toLocaleString()}</div>
                </div>
                <Badge variant="outline" className={`text-[10px] uppercase ${
                  o.status === "paid" || o.status === "assigned" ? "border-emerald-500/30 text-emerald-400" :
                  o.status === "pending" ? "border-[#ff7a00]/30 text-[#ff9a2f]" :
                  "border-red-500/30 text-red-400"
                }`}>{o.status}</Badge>
              </div>
            ))}
          </div>
        </div>

        <div className="glass rounded-xl p-6" data-testid="recent-certs-card">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display text-lg font-semibold">Certificates</div>
            <Link to="/dashboard/certificates" className="text-xs text-[#ff9a2f] hover:text-[#ff7a00]">View all</Link>
          </div>
          {certs.length === 0 && <div className="text-sm text-[#8a8a8a]">No certificates yet. Pass a challenge to earn one.</div>}
          <div className="space-y-2">
            {certs.slice(0, 4).map((c) => (
              <div key={c.id} className="flex items-center justify-between text-sm p-2.5 rounded-lg hover:bg-white/5">
                <div className="flex items-center gap-3">
                  <Award className="text-[#ff7a00]" size={18} />
                  <div>
                    <div className="text-[#f5f5f5]">{c.achievement_title}</div>
                    <div className="text-[10px] text-[#8a8a8a] font-mono">#{c.certificate_number}</div>
                  </div>
                </div>
                <div className="text-[#ff9a2f] font-display font-bold">${c.account_size.toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
